// =============================================================================
// Origin UI — src/pages/employees/EmployeeProfilePage.tsx
// Tabbed employee profile page with role-aware field visibility.
// HR Admin: sees all, can edit all fields. Employee: sees own full record, read-only.
// Manager: sees non-sensitive fields for direct reports, read-only.
// Phase 4 — Employee Records module.
// Updated: Batch 3 — added address fields, PersonalMobile, IsOmani, CountryEntity,
//          OqEmail display, passport + national ID fields on Family tab.
// Oct 2026: shows 'Working Week Follows' (read-only) when set in Admin → Leave → Working Week.
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/services/apiService';
import { letterApi, GeneratedLetterItem } from '@/services/letterApi';
import { useAuth } from '@/hooks/useAuth';
import { useModuleFlags } from '@/hooks/useModuleFlags';
import { complianceApi } from '@/services/complianceApi';
import type { ComplianceSubmissionListItem } from '@/services/complianceApi';
import { SubmissionViewer } from '@/components/compliance/SubmissionViewer';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import { LetterGenerationModal } from '@/components/LetterGenerationModal';
import { generateAssetPdf } from '@/services/reportPdfGenerator';
import styles from './employees.module.css';
import { VisaTab } from './VisaTab';
import { EvaluationsTab } from './EvaluationsTab';

// ─── Types ───

interface EmployeeDetail {
  employeeId: string;
  employeeNumber: string;
  entraObjectId: string | null;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  email: string;
  phone: string | null;
  profilePhotoUrl: string | null;
  personalEmail: string | null;
  personalMobile: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  nationality: string | null;
  isOmani: boolean | null;
  nationalIdNumber: string | null;
  nationalIdType: string | null;
  nationalIdExpiry: string | null;
  currentAddressLine1: string | null;
  currentAddressLine2: string | null;
  currentCity: string | null;
  currentStateRegion: string | null;
  currentPostalCode: string | null;
  currentCountry: string | null;
  jobTitle: string;
  departmentId: number;
  departmentName: string;
  officeLocationId: number;
  officeName: string;
  workingPatternOfficeLocationId: number | null;
  workingPatternOfficeName: string | null;
  countryEntity: string | null;
  managerId: string | null;
  managerName: string | null;
  employmentType: string;
  employmentStatus: string;
  joinDate: string;
  probationEndDate: string | null;
  confirmationDate: string | null;
  contractEndDate: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  exitNotes: string | null;
  baseSalary: number | null;
  salaryCurrency: string | null;
  payFrequency: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankIBAN: string | null;
  bankBranchName: string | null;
  bankSwiftCode: string | null;
  highestQualification: string | null;
  qualificationInstitution: string | null;
  qualificationYear: number | null;
  createdAt: string;
  updatedAt: string;
  emergencyContactCount: number;
  familyMemberCount: number;
  documentCount: number;
  salaryHistoryCount: number;
  bonusHistoryCount: number;
  oqEmployeeNumber: string | null;
  oqRequestedDate: string | null;
  oqRequestedBy: string | null;
  oqEmail: string | null;
  // PrePrd new fields
  fullName: string | null;
  preferredFirstName: string | null;
  preferredLastName: string | null;
  displayName: string | null;
  vistaEmployeeId: string | null;
  workMobile: string | null;
  officeExtension: string | null;
  speedDial: string | null;
  secondaryNationality: string | null;
  tertiaryNationality: string | null;
  passportNumber: string | null;
  permanentAddress: string | null;
  organisationalUnit: string | null;
  category: string | null;
  costCentre: string | null;
  isRevenueGenerator: boolean | null;
  departureDate: string | null;
  departureReason: string | null;
  bankIban: string | null;
  bankAccountName: string | null;
  bankCurrency: string | null;
  dewsCity: string | null;
  dewsCountryOfResidence: string | null;
  dewsNationality: string | null;
  insuranceProvider: string | null;
  insuranceType: string | null;
  insuranceCoverageStart: string | null;
  insuranceCoverageEnd: string | null;
  insuranceGroupId: string | null;
  visaCount: number;
  // Sensitive bucket (salary, banking, national ID) — present only when the
  // viewer is self, HRAdmin, or OQTMan. Flattened onto this object after fetch
  // (see loadEmployee) so existing field reads keep working.
  sensitive?: EmployeeSensitive | null;
}

interface EmployeeSensitive {
  baseSalary: number | null;
  salaryCurrency: string | null;
  payFrequency: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankIban: string | null;
  bankBranchName: string | null;
  bankSwiftCode: string | null;
  bankAccountName: string | null;
  bankCurrency: string | null;
  nationalIdNumber: string | null;
  nationalIdType: string | null;
  nationalIdExpiry: string | null;
}

interface EmergencyContact {
  emergencyContactId: string;
  contactName: string;
  relationship: string;
  phone: string;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateRegion: string | null;
  postalCode: string | null;
  country: string | null;
}

interface FamilyMember {
  familyMemberId: string;
  relationship: string;
  fullName: string;
  dateOfBirth: string | null;
  gender: string | null;
  phone: string | null;
  passportNumber: string | null;
  passportExpiry: string | null;
  passportCountry: string | null;
  nationalIdNumber: string | null;
  nationalIdType: string | null;
  nationalIdExpiry: string | null;
}

interface SalaryHistory {
  salaryHistoryId: string;
  effectiveDate: string;
  baseSalary: number;
  currency: string;
  changeReason: string | null;
  changedBy: string;
  changedAt: string;
}

interface BonusHistoryItem {
  bonusHistoryId: string;
  bonusYear: number;
  bonusAmount: number;
  currency: string;
  paidDate: string | null;
  notes: string | null;
  awardedBy: string;
  awardedAt: string;
}

interface DocumentItem {
  documentId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  category: string;
  description: string | null;
  uploadedBy: string;
  uploadedAt: string;
}

interface Department {
  departmentId: number;
  name: string;
}

interface OfficeLocation {
  officeLocationId: number;
  name: string;
}

interface EmployeeListItem {
  employeeId: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  preferredFirstName: string | null;
  preferredLastName: string | null;
  displayName: string | null;
  jobTitle: string;
  departmentName: string;
  officeName: string;
  employmentStatus: string;
}

type Tab = 'overview' | 'compensation' | 'family' | 'visa' | 'documents' | 'assets' | 'evaluations' | 'history' | 'compliance';

export function EmployeeProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { roles, userId } = useAuth();
  const { isModuleEnabled } = useModuleFlags();
  const complianceEnabled = isModuleEnabled('Compliance');
  const [employee, setEmployee] = useState<EmployeeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('overview');

  const isHR = roles.isHRAdmin;
  const isOQTMan = roles.isOQTMan;
  const isSelf = employee?.entraObjectId === userId;
  const canViewAll = isHR || isOQTMan;             // full read access (OQTMan is read-only)
  const isManagerOf = !canViewAll && !isSelf;      // else manager (backend enforces access)
  const canSeeSensitive = canViewAll || isSelf;    // sensitive data + tab visibility
  const [showLetterModal, setShowLetterModal] = useState(false);

  const [forbidden, setForbidden] = useState(false);

  useEffect(() => { if (id) loadEmployee(); }, [id]);

  async function loadEmployee() {
    try {
      setLoading(true);
      setError('');
      setForbidden(false);
      const data = await api.get<EmployeeDetail>(`/api/employees/${id}`);
      // Flatten the sensitive bucket onto the top level so existing field reads
      // (and the pass-through save logic) keep working. Absent for unauthorised
      // viewers, in which case these stay null and show as "—".
      if (data.sensitive) {
        const s = data.sensitive;
        data.baseSalary = s.baseSalary;
        data.salaryCurrency = s.salaryCurrency;
        data.payFrequency = s.payFrequency;
        data.bankName = s.bankName;
        data.bankAccountNumber = s.bankAccountNumber;
        data.bankIban = s.bankIban;
        data.bankIBAN = s.bankIban;
        data.bankBranchName = s.bankBranchName;
        data.bankSwiftCode = s.bankSwiftCode;
        data.bankAccountName = s.bankAccountName;
        data.bankCurrency = s.bankCurrency;
        data.nationalIdNumber = s.nationalIdNumber;
        data.nationalIdType = s.nationalIdType;
        data.nationalIdExpiry = s.nationalIdExpiry;
      }
      setEmployee(data);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
      } else {
        setError(err.message || 'Failed to load employee');
      }
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <LoadingSpinner message="Loading employee profile..." />;
  if (forbidden) return (
    <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
      <h2 style={{ color: '#0A2240', marginBottom: 8 }}>You don't have permission to view this profile</h2>
      <p style={{ color: '#566', marginBottom: 24 }}>
        Your access level doesn't allow you to see this employee's record. If you believe you should have access, contact HR or IT.
      </p>
      <button className={styles.backBtn} onClick={() => navigate('/employees')}>← Back to employees</button>
    </div>
  );
  if (error) return <ErrorBanner message={error} />;
  if (!employee) return <ErrorBanner message="Employee not found" />;

  const displayName = employee.displayName
    ? employee.displayName
    : employee.preferredFirstName
    ? `${employee.preferredFirstName} ${employee.preferredLastName || employee.lastName}`
    : employee.preferredName
    ? `${employee.preferredName} ${employee.lastName}`
    : `${employee.firstName} ${employee.lastName}`;
  const initials = ((employee.firstName?.[0] || '') + (employee.lastName?.[0] || '')).toUpperCase();

  return (
    <div>
      <div className={styles.profileHeader}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
        <div className={styles.profileHeaderContent}>
          <div className={styles.profileAvatar}>{initials}</div>
          <div className={styles.profileHeaderInfo}>
            <h1 className={styles.profileName}>{displayName}</h1>
            <div className={styles.profileMeta}>{employee.jobTitle} · {employee.departmentName} · {employee.officeName}</div>
            <div className={styles.profileBadges}>
              <span className={styles.empNumBadge}>{employee.employeeNumber}</span>
              <span className={`${styles.statusBadge} ${styles['status' + employee.employmentStatus]}`}>{employee.employmentStatus}</span>
              {employee.employmentType !== 'Permanent' && (
                <span className={`${styles.typeBadge} ${styles.typeContract}`}>{employee.employmentType}</span>
              )}
            </div>
          </div>
        </div>
        {(isHR || isSelf) && (
          <button className={styles.addSmallBtn} onClick={() => setShowLetterModal(true)} style={{ marginTop: '12px' }}>
            📄 Generate Letter
          </button>
        )}
      </div>

      {showLetterModal && employee && (
        <LetterGenerationModal
          employeeId={employee.employeeId}
          employeeName={`${employee.firstName} ${employee.lastName}`}
          officeName={employee.officeName}
          isHR={isHR}
          onClose={() => setShowLetterModal(false)}
          onGenerated={() => {}}
        />
      )}

      <div className={styles.profileTabs}>
        {([
          ['overview', 'Overview'],
          ...(canSeeSensitive ? [['compensation', 'Compensation']] : []),
          ...((canSeeSensitive || isManagerOf) ? [['family', 'Emergency Contacts']] : []),
          ...(canSeeSensitive ? [['visa', 'Visa & Permits']] : []),
          ...(canSeeSensitive ? [['documents', 'Documents']] : []),
          ...(canSeeSensitive ? [['assets', 'IT Assets']] : []),
          ...((canSeeSensitive || isManagerOf) ? [['evaluations', 'Evaluations']] : []),
          ...(canSeeSensitive ? [['history', 'History']] : []),
          ...(isSelf && complianceEnabled ? [['compliance', 'Compliance']] : []),
        ] as [Tab, string][]).map(([key, label]) => (
          <button key={key} className={`${styles.profileTab} ${tab === key ? styles.profileTabActive : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab employee={employee} canSeeSensitive={canSeeSensitive} isHR={isHR} isSelf={isSelf} canViewAll={canViewAll} onReload={loadEmployee} />}
      {tab === 'compensation' && canSeeSensitive && <CompensationTab employee={employee} isHR={isHR} canViewAll={canViewAll} onReload={loadEmployee} />}
      {tab === 'family' && (canSeeSensitive || isManagerOf) && <FamilyContactsTab employeeId={employee.employeeId} isHR={isHR} isSelf={isSelf} isManagerView={isManagerOf} employeeAddress={{ addressLine1: employee.currentAddressLine1, addressLine2: employee.currentAddressLine2, city: employee.currentCity, stateRegion: employee.currentStateRegion, postalCode: employee.currentPostalCode, country: employee.currentCountry }} />}
      {tab === 'visa' && canSeeSensitive && <VisaTab employeeId={employee.employeeId} isHR={isHR} />}
      {tab === 'documents' && canSeeSensitive && <DocumentsTab employeeId={employee.employeeId} isHR={isHR} />}
      {tab === 'assets' && canSeeSensitive && <AssetsTab employeeId={employee.employeeId} employee={employee} isHR={isHR} />}
      {tab === 'evaluations' && (canSeeSensitive || isManagerOf) && <EvaluationsTab employeeId={employee.employeeId} />}
      {tab === 'history' && canSeeSensitive && <HistoryTab employeeId={employee.employeeId} />}
      {tab === 'compliance' && isSelf && complianceEnabled && <ComplianceTab />}
    </div>
  );
}

// =============================================================================
// Overview Tab
// =============================================================================

function OverviewTab({ employee, canSeeSensitive, isHR, isSelf = false, canViewAll = false, onReload }: {
  employee: EmployeeDetail; canSeeSensitive: boolean; isHR: boolean; isSelf?: boolean; canViewAll?: boolean; onReload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [offices, setOffices] = useState<OfficeLocation[]>([]);
  const [allEmployees, setAllEmployees] = useState<EmployeeListItem[]>([]);
  const [refLoaded, setRefLoaded] = useState(false);
  const [statusWarning, setStatusWarning] = useState<string | null>(null);
  const [form, setForm] = useState(buildForm(employee));

  // ─── Self-service personal details editing ───
  const [selfEditing, setSelfEditing] = useState(false);
  const [selfSaving, setSelfSaving] = useState(false);
  const [selfError, setSelfError] = useState('');
  const [selfForm, setSelfForm] = useState({
    personalMobile: employee.personalMobile || '',
    personalEmail: employee.personalEmail || '',
    currentAddressLine1: employee.currentAddressLine1 || '',
    currentAddressLine2: employee.currentAddressLine2 || '',
    currentCity: employee.currentCity || '',
    currentStateRegion: employee.currentStateRegion || '',
    currentPostalCode: employee.currentPostalCode || '',
    currentCountry: employee.currentCountry || '',
    permanentAddress: employee.permanentAddress || '',
  });

  function sf(field: string, value: string) { setSelfForm(prev => ({ ...prev, [field]: value })); }

  function handleStartSelfEdit() {
    setSelfForm({
      personalMobile: employee.personalMobile || '',
      personalEmail: employee.personalEmail || '',
      currentAddressLine1: employee.currentAddressLine1 || '',
      currentAddressLine2: employee.currentAddressLine2 || '',
      currentCity: employee.currentCity || '',
      currentStateRegion: employee.currentStateRegion || '',
      currentPostalCode: employee.currentPostalCode || '',
      currentCountry: employee.currentCountry || '',
      permanentAddress: employee.permanentAddress || '',
    });
    setSelfError('');
    setSelfEditing(true);
  }

  async function handleSelfSave() {
    try {
      setSelfSaving(true); setSelfError('');
      await api.put('/api/employees/me/personal', {
        personalMobile: selfForm.personalMobile || null,
        personalEmail: selfForm.personalEmail || null,
        currentAddressLine1: selfForm.currentAddressLine1 || null,
        currentAddressLine2: selfForm.currentAddressLine2 || null,
        currentCity: selfForm.currentCity || null,
        currentStateRegion: selfForm.currentStateRegion || null,
        currentPostalCode: selfForm.currentPostalCode || null,
        currentCountry: selfForm.currentCountry || null,
        permanentAddress: selfForm.permanentAddress || null,
      });
      setSelfEditing(false); onReload();
    } catch (err: any) { setSelfError(err.message || 'Failed to save'); }
    finally { setSelfSaving(false); }
  }

  function buildForm(emp: EmployeeDetail) {
    return {
      firstName: emp.firstName || '', lastName: emp.lastName || '', preferredName: emp.preferredName || '', preferredFirstName: emp.preferredFirstName || '', preferredLastName: emp.preferredLastName || '', displayName: emp.displayName || '',
      jobTitle: emp.jobTitle || '', departmentId: emp.departmentId, officeLocationId: emp.officeLocationId,
      countryEntity: emp.countryEntity || '', managerId: emp.managerId || '',
      employmentType: emp.employmentType || 'Permanent', employmentStatus: emp.employmentStatus || 'Active',
      joinDate: emp.joinDate ? emp.joinDate.split('T')[0] : '',
      probationEndDate: emp.probationEndDate ? emp.probationEndDate.split('T')[0] : '',
      confirmationDate: emp.confirmationDate ? emp.confirmationDate.split('T')[0] : '',
      contractEndDate: emp.contractEndDate ? emp.contractEndDate.split('T')[0] : '',
      terminationDate: emp.terminationDate ? emp.terminationDate.split('T')[0] : '',
      terminationReason: emp.terminationReason || '', exitNotes: emp.exitNotes || '',
      email: emp.email || '', phone: emp.phone || '', personalEmail: emp.personalEmail || '',
      personalMobile: emp.personalMobile || '',
      dateOfBirth: emp.dateOfBirth ? emp.dateOfBirth.split('T')[0] : '',
      gender: emp.gender || '', nationality: emp.nationality || '', isOmani: emp.isOmani ?? false,
      secondaryNationality: emp.secondaryNationality || '', tertiaryNationality: emp.tertiaryNationality || '',
      nationalIdType: emp.nationalIdType || '', nationalIdNumber: emp.nationalIdNumber || '',
      nationalIdExpiry: emp.nationalIdExpiry ? emp.nationalIdExpiry.split('T')[0] : '',
      currentAddressLine1: emp.currentAddressLine1 || '', currentAddressLine2: emp.currentAddressLine2 || '',
      currentCity: emp.currentCity || '', currentStateRegion: emp.currentStateRegion || '',
      currentPostalCode: emp.currentPostalCode || '', currentCountry: emp.currentCountry || '',
      highestQualification: emp.highestQualification || '',
      qualificationInstitution: emp.qualificationInstitution || '',
      qualificationYear: emp.qualificationYear?.toString() || '',
      oqEmployeeNumber: emp.oqEmployeeNumber || '',
      vistaEmployeeId: emp.vistaEmployeeId || '',
      organisationalUnit: emp.organisationalUnit || '',
      category: emp.category || '',
      costCentre: emp.costCentre || '',
      isRevenueGenerator: emp.isRevenueGenerator ?? false,
      officeExtension: emp.officeExtension || '',
      speedDial: emp.speedDial || '',
      permanentAddress: emp.permanentAddress || '',
    };
  }

  function f(field: string, value: any) { setForm(prev => ({ ...prev, [field]: value })); }

  async function loadRefs() {
    if (refLoaded) return;
    try {
      const [depts, offs, emps] = await Promise.all([
        api.get<Department[]>('/api/reference/departments'),
        api.get<OfficeLocation[]>('/api/reference/office-locations'),
        api.get<EmployeeListItem[]>('/api/employees'),
      ]);
      setDepartments(depts); setOffices(offs);
      // Manager candidates: every current employee (Active, OnProbation, etc.), excluding leavers
      // and the person themselves. Always keep the existing manager so the current value isn't lost.
      const leaverStatuses = ['Resigned', 'Terminated', 'Departed'];
      setAllEmployees(emps.filter(e =>
        e.employeeId !== employee.employeeId &&
        (!leaverStatuses.includes(e.employmentStatus) || e.employeeId === employee.managerId)
      ));
      setRefLoaded(true);
    } catch (err: any) { setEditError('Failed to load reference data'); }
  }

  async function handleStartEdit() {
    setForm(buildForm(employee)); setEditError('');
    await loadRefs(); setEditing(true);
  }

  async function handleSave() {
    try {
      setSaving(true); setEditError('');
      await api.put(`/api/employees/${employee.employeeId}`, {
        firstName: form.firstName, lastName: form.lastName, preferredName: form.preferredName || null, preferredFirstName: form.preferredFirstName || null, preferredLastName: form.preferredLastName || null, displayName: form.displayName || null,
        jobTitle: form.jobTitle, departmentId: form.departmentId, officeLocationId: form.officeLocationId,
        countryEntity: form.countryEntity || null, managerId: form.managerId || null,
        employmentType: form.employmentType, employmentStatus: form.employmentStatus,
        joinDate: form.joinDate || null, probationEndDate: form.probationEndDate || null,
        confirmationDate: form.confirmationDate || null, contractEndDate: form.contractEndDate || null,
        terminationDate: form.terminationDate || null, terminationReason: form.terminationReason || null,
        exitNotes: form.exitNotes || null, email: form.email, phone: form.phone || null,
        personalEmail: form.personalEmail || null, personalMobile: form.personalMobile || null,
        dateOfBirth: form.dateOfBirth || null, gender: form.gender || null,
        nationality: form.nationality || null, isOmani: form.isOmani,
        secondaryNationality: form.secondaryNationality || null, tertiaryNationality: form.tertiaryNationality || null,
        nationalIdType: form.nationalIdType || null, nationalIdNumber: form.nationalIdNumber || null,
        nationalIdExpiry: form.nationalIdExpiry || null,
        currentAddressLine1: form.currentAddressLine1 || null, currentAddressLine2: form.currentAddressLine2 || null,
        currentCity: form.currentCity || null, currentStateRegion: form.currentStateRegion || null,
        currentPostalCode: form.currentPostalCode || null, currentCountry: form.currentCountry || null,
        highestQualification: form.highestQualification || null,
        qualificationInstitution: form.qualificationInstitution || null,
        qualificationYear: form.qualificationYear ? parseInt(form.qualificationYear) : null,
        baseSalary: employee.baseSalary, salaryCurrency: employee.salaryCurrency || null,
        payFrequency: employee.payFrequency || null, bankName: employee.bankName || null,
        bankAccountNumber: employee.bankAccountNumber || null, bankIBAN: employee.bankIBAN || null,
        bankBranchName: employee.bankBranchName || null, bankSwiftCode: employee.bankSwiftCode || null,
        oqEmployeeNumber: form.oqEmployeeNumber || null,
        vistaEmployeeId: form.vistaEmployeeId ? form.vistaEmployeeId.trim().toUpperCase() : null,
        organisationalUnit: form.organisationalUnit || null,
        category: form.category || null,
        costCentre: form.costCentre || null,
        isRevenueGenerator: form.isRevenueGenerator,
        officeExtension: form.officeExtension || null,
        speedDial: form.speedDial || null,
        permanentAddress: form.permanentAddress || null,
      });
      setEditing(false); onReload();
    } catch (err: any) { setEditError(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  function addrStr(l1: string | null, l2: string | null, city: string | null, state: string | null, postal: string | null, country: string | null) {
    return [l1, l2, city, state && postal ? `${state} ${postal}` : (state || postal), country].filter(Boolean).join(', ') || '—';
  }

  return (
    <div className={styles.tabContent}>
      {isHR && !editing && (
        <div className={styles.editActionBar}>
          <button className={styles.editBtn} onClick={handleStartEdit}>✏️ Edit Employee</button>
        </div>
      )}
      {isHR && editing && (
        <div className={styles.editActionBar}>
          {editError && <span className={styles.editErrorInline}>{editError}</span>}
          <button className={styles.cancelSmallBtn} onClick={() => { setEditing(false); setEditError(''); }} disabled={saving}>Cancel</button>
          <button className={styles.saveSmallBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save Changes'}</button>
        </div>
      )}
      {isSelf && !isHR && !selfEditing && (
        <div className={styles.editActionBar}>
          <button className={styles.editBtn} onClick={handleStartSelfEdit}>✏️ Edit Personal Details</button>
        </div>
      )}
      {isSelf && !isHR && selfEditing && (
        <div className={styles.editActionBar}>
          {selfError && <span className={styles.editErrorInline}>{selfError}</span>}
          <button className={styles.cancelSmallBtn} onClick={() => { setSelfEditing(false); setSelfError(''); }} disabled={selfSaving}>Cancel</button>
          <button className={styles.saveSmallBtn} onClick={handleSelfSave} disabled={selfSaving}>{selfSaving ? 'Saving...' : '💾 Save Changes'}</button>
        </div>
      )}

      {/* Employment */}
      <Card>
        <h3 className={styles.sectionTitle}>Employment</h3>
        {editing ? (
          <div className={styles.fieldGrid}>
            <EditInput label="Job Title *" value={form.jobTitle} onChange={v => f('jobTitle', v)} />
            <EditSelect label="Department *" value={String(form.departmentId)} onChange={v => f('departmentId', parseInt(v))}>
              {departments.map(d => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
            </EditSelect>
            <EditSelect label="Office *" value={String(form.officeLocationId)} onChange={v => f('officeLocationId', parseInt(v))}>
              {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
            </EditSelect>
            <EditInput label="Country Entity" value={form.countryEntity} onChange={v => f('countryEntity', v)} placeholder="e.g. OQT Dubai" />
            <EditSelect label="Manager" value={form.managerId} onChange={v => f('managerId', v)}>
              <option value="">— None —</option>
              {allEmployees.map(e => {
                const name = e.displayName || `${e.preferredFirstName || e.preferredName || e.firstName} ${e.preferredLastName || e.lastName}`;
                return <option key={e.employeeId} value={e.employeeId}>{name} ({e.employeeNumber})</option>;
              })}
            </EditSelect>
            <EditSelect label="Employment Type *" value={form.employmentType} onChange={v => f('employmentType', v)}>
              <option value="Permanent">Permanent</option>
              <option value="FTC">FTC</option>
              <option value="Intern">Intern</option>
            </EditSelect>
            <EditSelect label="Status *" value={form.employmentStatus} onChange={v => {
              if ((v === 'Resigned' || v === 'Terminated') && employee.employmentStatus !== 'Resigned' && employee.employmentStatus !== 'Terminated') { setStatusWarning(v); }
              else { f('employmentStatus', v); }
            }}>
              <option value="Active">Active</option>
              <option value="OnProbation">On Probation</option>
              <option value="Resigned">Resigned</option>
              <option value="Terminated">Terminated</option>
            </EditSelect>
            <EditInput label="Join Date *" type="date" value={form.joinDate} onChange={v => f('joinDate', v)} />
            <EditInput label="OQT ID (login code)" value={form.vistaEmployeeId} onChange={v => f('vistaEmployeeId', v.toUpperCase())} placeholder="e.g. MSJ (from MSJ@omantrading.com)" />
            <EditInput label="OQ Number" value={form.oqEmployeeNumber} onChange={v => f('oqEmployeeNumber', v)} placeholder="e.g. 7011656" />
            <EditInput label="Org Unit" value={form.organisationalUnit} onChange={v => f('organisationalUnit', v)} />
            <EditInput label="Category" value={form.category} onChange={v => f('category', v)} placeholder="e.g. Middle & Back Office" />
            <EditInput label="Cost Centre" value={form.costCentre} onChange={v => f('costCentre', v)} placeholder="e.g. FIN - DUBAI" />
            <div className={styles.formField}>
              <label className={styles.formLabel}>Revenue Generator</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '6px' }}>
                <input type="checkbox" checked={form.isRevenueGenerator} onChange={e => f('isRevenueGenerator', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                <span style={{ fontSize: '13px', color: '#4A5568' }}>Employee generates revenue</span>
              </div>
            </div>
            <EditInput label="Probation End" type="date" value={form.probationEndDate} onChange={v => f('probationEndDate', v)} />
            <EditInput label="Confirmation Date" type="date" value={form.confirmationDate} onChange={v => f('confirmationDate', v)} />
            {form.employmentType === 'FTC' && (
              <EditInput label="Contract End Date" type="date" value={form.contractEndDate} onChange={v => f('contractEndDate', v)} />
            )}
            {(form.employmentStatus === 'Resigned' || form.employmentStatus === 'Terminated') && (
              <>
                <EditInput label="Termination Date" type="date" value={form.terminationDate} onChange={v => f('terminationDate', v)} />
                <EditInput label="Termination Reason" value={form.terminationReason} onChange={v => f('terminationReason', v)} />
                <EditInput label="Exit Notes" value={form.exitNotes} onChange={v => f('exitNotes', v)} />
              </>
            )}
          </div>
        ) : (
          <div className={styles.fieldGrid}>
            <Field label="Job Title" value={employee.jobTitle} />
            <Field label="Department" value={employee.departmentName} />
            <Field label="Office" value={employee.officeName} />
            {employee.workingPatternOfficeName && (
              <Field label="Working Week Follows" value={`${employee.workingPatternOfficeName} (public holidays: ${employee.officeName})`} />
            )}
            <Field label="Country Entity" value={employee.countryEntity || '—'} />
            <Field label="Manager" value={employee.managerName || '—'} />
            <Field label="Employment Type" value={employee.employmentType} />
            <Field label="Status" value={employee.employmentStatus} />
            {employee.organisationalUnit && <Field label="Org Unit" value={employee.organisationalUnit} />}
            <Field label="OQT ID" value={employee.vistaEmployeeId || '—'} />
            <Field label="OQ Number" value={employee.oqEmployeeNumber || '—'} />
            {employee.oqEmail && <Field label="OQ Email" value={employee.oqEmail} />}
            {canViewAll && employee.category && <Field label="Category" value={employee.category} />}
            {canViewAll && employee.costCentre && <Field label="Cost Centre" value={employee.costCentre} />}
            {canViewAll && employee.isRevenueGenerator !== null && <Field label="Revenue Generator" value={employee.isRevenueGenerator ? 'Yes' : 'No'} />}
            {canViewAll && employee.departureDate && <Field label="Departure Date" value={formatDate(employee.departureDate)} />}
            {canViewAll && employee.departureReason && <Field label="Departure Reason" value={employee.departureReason} />}
            <Field label="Join Date" value={formatDate(employee.joinDate)} />
            <Field label="Probation End" value={formatDate(employee.probationEndDate)} />
            <Field label="Confirmation Date" value={formatDate(employee.confirmationDate)} />
            {employee.contractEndDate && <Field label="Contract End" value={formatDate(employee.contractEndDate)} />}
            {canSeeSensitive && employee.terminationDate && (
              <>
                <Field label="Termination Date" value={formatDate(employee.terminationDate)} />
                <Field label="Termination Reason" value={employee.terminationReason || '—'} />
              </>
            )}
          </div>
        )}
      </Card>

      {/* Personal Information */}
      <Card>
        <h3 className={styles.sectionTitle}>Personal Information</h3>
        {editing ? (
          <div className={styles.fieldGrid}>
            <EditInput label="First Name *" value={form.firstName} onChange={v => f('firstName', v)} />
            <EditInput label="Last Name *" value={form.lastName} onChange={v => f('lastName', v)} />
            <EditInput label="Preferred First Name" value={form.preferredFirstName} onChange={v => f('preferredFirstName', v)} placeholder="e.g. Lou" />
            <EditInput label="Preferred Last Name" value={form.preferredLastName} onChange={v => f('preferredLastName', v)} />
            {isHR && <EditInput label="Display Name" value={form.displayName} onChange={v => f('displayName', v)} placeholder="Override shown name (optional)" />}
            <EditInput label="Email *" value={form.email} onChange={v => f('email', v)} />
            <EditInput label="Work Phone" value={form.phone} onChange={v => f('phone', v)} />
            <EditInput label="Extension" value={form.officeExtension} onChange={v => f('officeExtension', v)} />
            <EditInput label="Speed Dial" value={form.speedDial} onChange={v => f('speedDial', v)} />
            <EditInput label="Personal Mobile" value={form.personalMobile} onChange={v => f('personalMobile', v)} placeholder="+971 50 000 0000" />
            <EditInput label="Personal Email" value={form.personalEmail} onChange={v => f('personalEmail', v)} />
            <EditInput label="Date of Birth" type="date" value={form.dateOfBirth} onChange={v => f('dateOfBirth', v)} />
            <EditSelect label="Gender" value={form.gender} onChange={v => f('gender', v)}>
              <option value="">— Select —</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </EditSelect>
            <EditInput label="Nationality" value={form.nationality} onChange={v => f('nationality', v)} />
            <EditInput label="Secondary Nationality" value={form.secondaryNationality} onChange={v => f('secondaryNationality', v)} />
            <EditInput label="Third Nationality" value={form.tertiaryNationality} onChange={v => f('tertiaryNationality', v)} />
            <div className={styles.formField}>
              <label className={styles.formLabel}>Omani National</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '6px' }}>
                <input type="checkbox" checked={form.isOmani} onChange={e => f('isOmani', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                <span style={{ fontSize: '13px', color: '#4A5568' }}>Employee holds Omani nationality</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.fieldGrid}>
            <Field label="Full Name" value={employee.fullName || `${employee.firstName} ${employee.lastName}`} />
            <Field label="Preferred Name" value={employee.preferredFirstName ? `${employee.preferredFirstName}${employee.preferredLastName ? ' ' + employee.preferredLastName : ''}` : (employee.preferredName || '—')} />
            {employee.displayName && <Field label="Display Name" value={employee.displayName} />}
            <Field label="Email" value={employee.email} />
            <Field label="Work Phone" value={employee.phone || '—'} />
            <Field label="Extension" value={employee.officeExtension || '—'} />
            <Field label="Speed Dial" value={employee.speedDial || '—'} />
            {canSeeSensitive && (
              <>
                {selfEditing ? (
                  <EditInput label="Personal Mobile" value={selfForm.personalMobile} onChange={v => sf('personalMobile', v)} />
                ) : (
                  <Field label="Personal Mobile" value={employee.personalMobile || '—'} />
                )}
                {employee.workMobile && <Field label="Work Mobile" value={employee.workMobile} />}
                {selfEditing ? (
                  <EditInput label="Personal Email" type="email" value={selfForm.personalEmail} onChange={v => sf('personalEmail', v)} />
                ) : (
                  <Field label="Personal Email" value={employee.personalEmail || '—'} />
                )}
                <Field label="Date of Birth" value={formatDate(employee.dateOfBirth)} />
                <Field label="Gender" value={employee.gender || '—'} />
                <Field label="Nationality" value={employee.nationality || '—'} />
                <Field label="2nd Nationality" value={employee.secondaryNationality || '—'} />
                <Field label="3rd Nationality" value={employee.tertiaryNationality || '—'} />
                <Field label="Omani National" value={employee.isOmani ? 'Yes' : 'No'} />
                {employee.passportNumber && <Field label="Passport Number" value={employee.passportNumber} />}
              </>
            )}
          </div>
        )}
      </Card>

      {/* Identification */}
      {canSeeSensitive && (
        <Card>
          <h3 className={styles.sectionTitle}>Identification</h3>
          {editing ? (
            <div className={styles.fieldGrid}>
              <EditInput label="ID Type" value={form.nationalIdType} onChange={v => f('nationalIdType', v)} placeholder="Passport, National ID, Visa..." />
              <EditInput label="ID Number" value={form.nationalIdNumber} onChange={v => f('nationalIdNumber', v)} />
              <EditInput label="Expiry Date" type="date" value={form.nationalIdExpiry} onChange={v => f('nationalIdExpiry', v)} />
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <Field label="ID Type" value={employee.nationalIdType || '—'} />
              <Field label="ID Number" value={employee.nationalIdNumber || '—'} />
              <Field label="Expiry Date" value={formatDate(employee.nationalIdExpiry)} />
            </div>
          )}
        </Card>
      )}

      {/* Current Address */}
      {canSeeSensitive && (
        <Card>
          <h3 className={styles.sectionTitle}>Current Address</h3>
          {editing ? (
            <div className={styles.fieldGrid}>
              <EditInput label="Address Line 1" value={form.currentAddressLine1} onChange={v => f('currentAddressLine1', v)} placeholder="Street address" />
              <EditInput label="Address Line 2" value={form.currentAddressLine2} onChange={v => f('currentAddressLine2', v)} placeholder="Apartment, floor, etc." />
              <EditInput label="City" value={form.currentCity} onChange={v => f('currentCity', v)} />
              <EditInput label="State / Region" value={form.currentStateRegion} onChange={v => f('currentStateRegion', v)} />
              <EditInput label="Postal Code" value={form.currentPostalCode} onChange={v => f('currentPostalCode', v)} />
              <EditInput label="Country" value={form.currentCountry} onChange={v => f('currentCountry', v)} />
            </div>
          ) : selfEditing ? (
            <div className={styles.fieldGrid}>
              <EditInput label="Address Line 1" value={selfForm.currentAddressLine1} onChange={v => sf('currentAddressLine1', v)} placeholder="Street address" />
              <EditInput label="Address Line 2" value={selfForm.currentAddressLine2} onChange={v => sf('currentAddressLine2', v)} placeholder="Apartment, floor, etc." />
              <EditInput label="City" value={selfForm.currentCity} onChange={v => sf('currentCity', v)} />
              <EditInput label="State / Region" value={selfForm.currentStateRegion} onChange={v => sf('currentStateRegion', v)} />
              <EditInput label="Postal Code" value={selfForm.currentPostalCode} onChange={v => sf('currentPostalCode', v)} />
              <EditInput label="Country" value={selfForm.currentCountry} onChange={v => sf('currentCountry', v)} />
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <Field label="Address" value={addrStr(employee.currentAddressLine1, employee.currentAddressLine2, employee.currentCity, employee.currentStateRegion, employee.currentPostalCode, employee.currentCountry)} />
            </div>
          )}
        </Card>
      )}

      {/* Permanent Address */}
      {canSeeSensitive && (
        <Card>
          <h3 className={styles.sectionTitle}>Permanent Address</h3>
          <div className={styles.fieldGrid}>
            {editing ? (
              <EditInput label="Address" value={form.permanentAddress} onChange={v => f('permanentAddress', v)} placeholder="Permanent / home country address" />
            ) : selfEditing ? (
              <EditInput label="Address" value={selfForm.permanentAddress} onChange={v => sf('permanentAddress', v)} placeholder="Permanent / home country address" />
            ) : (
              <Field label="Address" value={employee.permanentAddress || '—'} />
            )}
          </div>
        </Card>
      )}

      {/* Education */}
      {canSeeSensitive && (
        <Card>
          <h3 className={styles.sectionTitle}>Education</h3>
          {editing ? (
            <div className={styles.fieldGrid}>
              <EditInput label="Qualification" value={form.highestQualification} onChange={v => f('highestQualification', v)} placeholder="BSc, MBA, PhD..." />
              <EditInput label="Institution" value={form.qualificationInstitution} onChange={v => f('qualificationInstitution', v)} />
              <EditInput label="Year" type="number" value={form.qualificationYear} onChange={v => f('qualificationYear', v)} placeholder="e.g. 2018" />
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <Field label="Qualification" value={employee.highestQualification || '—'} />
              <Field label="Institution" value={employee.qualificationInstitution || '—'} />
              <Field label="Year" value={employee.qualificationYear?.toString() || '—'} />
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={statusWarning !== null}
        title="Change Employment Status"
        message="Changing status to Resigned or Terminated is normally done via an Offboarding request which ensures proper approval workflow and IT task generation. Are you sure you want to change the status manually?"
        confirmLabel="Yes, change status" confirmVariant="danger"
        onConfirm={() => { if (statusWarning) f('employmentStatus', statusWarning); setStatusWarning(null); }}
        onCancel={() => setStatusWarning(null)}
      />

      <div className={styles.systemInfo}>
        Entra ID: {employee.entraObjectId || 'Not linked'} · Created: {formatDateTime(employee.createdAt)} · Updated: {formatDateTime(employee.updatedAt)}
      </div>
    </div>
  );
}

// =============================================================================
// Compensation Tab
// =============================================================================

function CompensationTab({ employee, isHR, canViewAll = false, onReload }: { employee: EmployeeDetail; isHR: boolean; canViewAll?: boolean; onReload: () => void }) {
  const [bonuses, setBonuses] = useState<BonusHistoryItem[]>([]);
  const [loadingBonuses, setLoadingBonuses] = useState(true);
  const [error, setError] = useState('');
  const [addingBonus, setAddingBonus] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [bonusForm, setBonusForm] = useState({ bonusYear: new Date().getFullYear(), bonusAmount: '', currency: 'USD', paidDate: '', notes: '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [compForm, setCompForm] = useState({
    baseSalary: employee.baseSalary != null ? String(employee.baseSalary) : '',
    salaryCurrency: employee.salaryCurrency || 'USD', payFrequency: employee.payFrequency || 'Monthly',
    bankName: employee.bankName || '', bankAccountNumber: employee.bankAccountNumber || '',
    bankIBAN: employee.bankIBAN || '', bankBranchName: employee.bankBranchName || '', bankSwiftCode: employee.bankSwiftCode || '',
    bankAccountName: employee.bankAccountName || '', bankCurrency: employee.bankCurrency || '',
    insuranceProvider: employee.insuranceProvider || '', insuranceType: employee.insuranceType || '',
    insuranceCoverageStart: employee.insuranceCoverageStart ? employee.insuranceCoverageStart.substring(0, 10) : '',
    insuranceCoverageEnd: employee.insuranceCoverageEnd ? employee.insuranceCoverageEnd.substring(0, 10) : '',
    insuranceGroupId: employee.insuranceGroupId || '',
    dewsCity: employee.dewsCity || '', dewsCountryOfResidence: employee.dewsCountryOfResidence || '',
    dewsNationality: employee.dewsNationality || '',
  });

  useEffect(() => { loadBonuses(); }, [employee.employeeId]);

  async function loadBonuses() {
    try {
      setLoadingBonuses(true);
      setBonuses(await api.get<BonusHistoryItem[]>(`/api/employees/${employee.employeeId}/bonus-history`));
    } catch (err: any) { setError(err.message); }
    finally { setLoadingBonuses(false); }
  }

  async function handleSaveComp() {
    try {
      setSaving(true); setEditError('');
      await api.put(`/api/employees/${employee.employeeId}`, {
        baseSalary: compForm.baseSalary ? parseFloat(compForm.baseSalary) : null,
        salaryCurrency: compForm.salaryCurrency || null, payFrequency: compForm.payFrequency || null,
        bankName: compForm.bankName || null, bankAccountNumber: compForm.bankAccountNumber || null,
        bankIBAN: compForm.bankIBAN || null, bankBranchName: compForm.bankBranchName || null,
        bankSwiftCode: compForm.bankSwiftCode || null,
        firstName: employee.firstName, lastName: employee.lastName, preferredName: employee.preferredName || null, preferredFirstName: employee.preferredFirstName || null, preferredLastName: employee.preferredLastName || null, displayName: employee.displayName || null,
        email: employee.email, phone: employee.phone || null, personalEmail: employee.personalEmail || null,
        personalMobile: employee.personalMobile || null, dateOfBirth: employee.dateOfBirth || null,
        gender: employee.gender || null, nationality: employee.nationality || null, isOmani: employee.isOmani ?? false,
        nationalIdType: employee.nationalIdType || null, nationalIdNumber: employee.nationalIdNumber || null,
        nationalIdExpiry: employee.nationalIdExpiry || null,
        currentAddressLine1: employee.currentAddressLine1 || null, currentAddressLine2: employee.currentAddressLine2 || null,
        currentCity: employee.currentCity || null, currentStateRegion: employee.currentStateRegion || null,
        currentPostalCode: employee.currentPostalCode || null, currentCountry: employee.currentCountry || null,
        jobTitle: employee.jobTitle, departmentId: employee.departmentId, officeLocationId: employee.officeLocationId,
        countryEntity: employee.countryEntity || null, managerId: employee.managerId || null,
        employmentType: employee.employmentType, employmentStatus: employee.employmentStatus,
        joinDate: employee.joinDate, probationEndDate: employee.probationEndDate || null,
        confirmationDate: employee.confirmationDate || null, contractEndDate: employee.contractEndDate || null,
        terminationDate: employee.terminationDate || null, terminationReason: employee.terminationReason || null,
        exitNotes: employee.exitNotes || null, highestQualification: employee.highestQualification || null,
        qualificationInstitution: employee.qualificationInstitution || null, qualificationYear: employee.qualificationYear || null,
        bankAccountName: compForm.bankAccountName || null, bankCurrency: compForm.bankCurrency || null,
        insuranceProvider: compForm.insuranceProvider || null, insuranceType: compForm.insuranceType || null,
        insuranceCoverageStart: compForm.insuranceCoverageStart || null, insuranceCoverageEnd: compForm.insuranceCoverageEnd || null,
        insuranceGroupId: compForm.insuranceGroupId || null,
        dewsCity: compForm.dewsCity || null, dewsCountryOfResidence: compForm.dewsCountryOfResidence || null,
        dewsNationality: compForm.dewsNationality || null,
      });
      setEditing(false); onReload();
    } catch (err: any) { setEditError(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function saveBonus() {
    try {
      setError('');
      await api.post(`/api/employees/${employee.employeeId}/bonus-history`, {
        ...bonusForm, bonusAmount: parseFloat(bonusForm.bonusAmount) || 0, paidDate: bonusForm.paidDate || null,
      });
      setAddingBonus(false);
      setBonusForm({ bonusYear: new Date().getFullYear(), bonusAmount: '', currency: 'USD', paidDate: '', notes: '' });
      await loadBonuses();
    } catch (err: any) { setError(err.message); }
  }

  async function handleDeleteBonus() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/employees/${employee.employeeId}/bonus-history/${deleteTarget.id}`);
      setDeleteTarget(null); await loadBonuses();
    } catch (err: any) { setError(err.message); setDeleteTarget(null); }
  }

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {isHR && !editing && <div className={styles.editActionBar}><button className={styles.editBtn} onClick={() => { setEditError(''); setEditing(true); }}>✏️ Edit Compensation</button></div>}
      {isHR && editing && (
        <div className={styles.editActionBar}>
          {editError && <span className={styles.editErrorInline}>{editError}</span>}
          <button className={styles.cancelSmallBtn} onClick={() => { setEditing(false); setEditError(''); }} disabled={saving}>Cancel</button>
          <button className={styles.saveSmallBtn} onClick={handleSaveComp} disabled={saving}>{saving ? 'Saving...' : '💾 Save Changes'}</button>
        </div>
      )}

      <Card>
        <h3 className={styles.sectionTitle}>Current Salary</h3>
        {editing ? (
          <div className={styles.fieldGrid}>
            <EditInput label="Annual Base Salary" value={compForm.baseSalary} onChange={v => setCompForm({ ...compForm, baseSalary: v })} type="number" placeholder="e.g. 85000" />
            <EditInput label="Currency" value={compForm.salaryCurrency} onChange={v => setCompForm({ ...compForm, salaryCurrency: v })} placeholder="USD, GBP, OMR..." />
            <EditSelect label="Pay Frequency" value={compForm.payFrequency} onChange={v => setCompForm({ ...compForm, payFrequency: v })}>
              <option value="Monthly">Monthly</option><option value="Bi-Weekly">Bi-Weekly</option>
              <option value="Weekly">Weekly</option><option value="Annual">Annual</option>
            </EditSelect>
          </div>
        ) : (
          <div className={styles.fieldGrid}>
            <Field label="Annual Base Salary" value={employee.baseSalary != null ? `${employee.salaryCurrency || ''} ${employee.baseSalary.toLocaleString()}` : '—'} />
            <Field label="Pay Frequency" value={employee.payFrequency || '—'} />
          </div>
        )}
      </Card>

      <Card>
        <h3 className={styles.sectionTitle}>Banking Details</h3>
        {editing ? (
          <div className={styles.fieldGrid}>
            <EditInput label="Bank Name" value={compForm.bankName} onChange={v => setCompForm({ ...compForm, bankName: v })} />
            <EditInput label="Account Number" value={compForm.bankAccountNumber} onChange={v => setCompForm({ ...compForm, bankAccountNumber: v })} />
            <EditInput label="IBAN" value={compForm.bankIBAN} onChange={v => setCompForm({ ...compForm, bankIBAN: v })} />
            <EditInput label="Branch" value={compForm.bankBranchName} onChange={v => setCompForm({ ...compForm, bankBranchName: v })} />
            <EditInput label="SWIFT Code" value={compForm.bankSwiftCode} onChange={v => setCompForm({ ...compForm, bankSwiftCode: v })} />
            <EditInput label="Account Name" value={compForm.bankAccountName} onChange={v => setCompForm({ ...compForm, bankAccountName: v })} />
            <EditInput label="Currency" value={compForm.bankCurrency} onChange={v => setCompForm({ ...compForm, bankCurrency: v })} placeholder="AED, USD, GBP..." />
          </div>
        ) : (
          <div className={styles.fieldGrid}>
            <Field label="Bank Name" value={employee.bankName || '—'} />
            <Field label="Account Name" value={employee.bankAccountName || '—'} />
            <Field label="Account Number" value={employee.bankAccountNumber || '—'} />
            <Field label="IBAN" value={employee.bankIban || employee.bankIBAN || '—'} />
            <Field label="Branch" value={employee.bankBranchName || '—'} />
            <Field label="SWIFT Code" value={employee.bankSwiftCode || '—'} />
            <Field label="Currency" value={employee.bankCurrency || '—'} />
          </div>
        )}
        {editing && <p className={styles.editHint}>If you change the salary, the previous amount will be automatically recorded in Salary History.</p>}
      </Card>

      <Card>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Bonus History</h3>
          {isHR && <button className={styles.addSmallBtn} onClick={() => setAddingBonus(true)}>+ Award Bonus</button>}
        </div>
        {addingBonus && (
          <div className={styles.inlineForm}>
            <div className={styles.fieldGrid}>
              <FormInput label="Year *" type="number" value={String(bonusForm.bonusYear)} onChange={v => setBonusForm({ ...bonusForm, bonusYear: parseInt(v) || new Date().getFullYear() })} />
              <FormInput label="Amount *" value={bonusForm.bonusAmount} onChange={v => setBonusForm({ ...bonusForm, bonusAmount: v })} placeholder="e.g., 15000" />
              <FormInput label="Currency" value={bonusForm.currency} onChange={v => setBonusForm({ ...bonusForm, currency: v })} placeholder="USD" />
              <FormInput label="Paid Date" type="date" value={bonusForm.paidDate} onChange={v => setBonusForm({ ...bonusForm, paidDate: v })} />
              <FormInput label="Notes" value={bonusForm.notes} onChange={v => setBonusForm({ ...bonusForm, notes: v })} placeholder="e.g., Annual discretionary bonus" />
            </div>
            <div className={styles.inlineFormActions}>
              <button className={styles.cancelSmallBtn} onClick={() => setAddingBonus(false)}>Cancel</button>
              <button className={styles.saveSmallBtn} onClick={saveBonus}>Save</button>
            </div>
          </div>
        )}
        {loadingBonuses ? <p className={styles.emptyText}>Loading...</p>
          : bonuses.length === 0 && !addingBonus ? <p className={styles.emptyText}>No bonus awards recorded.</p>
          : (
            <div className={styles.subList}>
              {bonuses.map(b => (
                <div key={b.bonusHistoryId} className={styles.subListItem}>
                  <div>
                    <div className={styles.subListName}>{b.currency} {b.bonusAmount.toLocaleString()} — {b.bonusYear}</div>
                    <div className={styles.subListMeta}>{b.paidDate ? `Paid ${formatDate(b.paidDate)}` : 'Not yet paid'}{b.notes ? ` · ${b.notes}` : ''}</div>
                  </div>
                  {isHR && <button className={styles.removeBtn} onClick={() => setDeleteTarget({ id: b.bonusHistoryId, label: `${b.currency} ${b.bonusAmount.toLocaleString()} (${b.bonusYear})` })}>Remove</button>}
                </div>
              ))}
            </div>
          )}
      </Card>

      {/* Insurance — HR or OQTMan */}
      {canViewAll && (
        <Card>
          <h3 className={styles.sectionTitle}>Medical Insurance</h3>
          {editing ? (
            <div className={styles.fieldGrid}>
              <EditInput label="Provider" value={compForm.insuranceProvider} onChange={v => setCompForm({ ...compForm, insuranceProvider: v })} placeholder="e.g. CIGNA NEURON" />
              <EditInput label="Type" value={compForm.insuranceType} onChange={v => setCompForm({ ...compForm, insuranceType: v })} placeholder="e.g. Family, Single" />
              <EditInput label="Coverage Start" type="date" value={compForm.insuranceCoverageStart} onChange={v => setCompForm({ ...compForm, insuranceCoverageStart: v })} />
              <EditInput label="Coverage End" type="date" value={compForm.insuranceCoverageEnd} onChange={v => setCompForm({ ...compForm, insuranceCoverageEnd: v })} />
              <EditInput label="Group ID" value={compForm.insuranceGroupId} onChange={v => setCompForm({ ...compForm, insuranceGroupId: v })} />
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <Field label="Provider" value={employee.insuranceProvider || '—'} />
              <Field label="Type" value={employee.insuranceType || '—'} />
              <Field label="Coverage Start" value={formatDate(employee.insuranceCoverageStart)} />
              <Field label="Coverage End" value={formatDate(employee.insuranceCoverageEnd)} />
              <Field label="Group ID" value={employee.insuranceGroupId || '—'} />
            </div>
          )}
        </Card>
      )}

      {/* DEWS — HR or OQTMan */}
      {canViewAll && (
        <Card>
          <h3 className={styles.sectionTitle}>DEWS Registration</h3>
          {editing ? (
            <div className={styles.fieldGrid}>
              <EditInput label="City" value={compForm.dewsCity} onChange={v => setCompForm({ ...compForm, dewsCity: v })} placeholder="e.g. Dubai" />
              <EditInput label="Country of Residence" value={compForm.dewsCountryOfResidence} onChange={v => setCompForm({ ...compForm, dewsCountryOfResidence: v })} placeholder="e.g. ARE" />
              <EditInput label="Nationality" value={compForm.dewsNationality} onChange={v => setCompForm({ ...compForm, dewsNationality: v })} placeholder="e.g. GBR" />
            </div>
          ) : (
            <div className={styles.fieldGrid}>
              <Field label="City" value={employee.dewsCity || '—'} />
              <Field label="Country of Residence" value={employee.dewsCountryOfResidence || '—'} />
              <Field label="Nationality" value={employee.dewsNationality || '—'} />
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog open={deleteTarget !== null} title="Remove Bonus Award"
        message={`Remove bonus ${deleteTarget?.label}? This cannot be undone.`}
        confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDeleteBonus} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}

// =============================================================================
// Family & Contacts Tab — with passport + national ID fields
// =============================================================================

function FamilyContactsTab({ employeeId, isHR, isSelf = false, isManagerView = false, employeeAddress }: { employeeId: string; isHR: boolean; isSelf?: boolean; isManagerView?: boolean; employeeAddress?: { addressLine1: string | null; addressLine2: string | null; city: string | null; stateRegion: string | null; postalCode: string | null; country: string | null } }) {
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [family, setFamily] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingContact, setAddingContact] = useState(false);
  const [addingFamily, setAddingFamily] = useState(false);
  const [expandedFamily, setExpandedFamily] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const [contactForm, setContactForm] = useState({ contactName: '', relationship: '', phone: '', email: '', addressLine1: '', addressLine2: '', city: '', stateRegion: '', postalCode: '', country: '' });
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [familyForm, setFamilyForm] = useState({
    fullName: '', relationship: 'Spouse', dateOfBirth: '', gender: '', phone: '',
    passportNumber: '', passportExpiry: '', passportCountry: '',
    nationalIdNumber: '', nationalIdType: '', nationalIdExpiry: '',
  });

  useEffect(() => { load(); }, [employeeId]);

  async function load() {
    try {
      setLoading(true);
      const [c, f] = await Promise.all([
        api.get<EmergencyContact[]>(`/api/employees/${employeeId}/emergency-contacts`),
        api.get<FamilyMember[]>(`/api/employees/${employeeId}/family`),
      ]);
      setContacts(c); setFamily(f);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function saveContact() {
    try {
      setError('');
      await api.post(`/api/employees/${employeeId}/emergency-contacts`, contactForm);
      setAddingContact(false); setContactForm({ contactName: '', relationship: '', phone: '', email: '', addressLine1: '', addressLine2: '', city: '', stateRegion: '', postalCode: '', country: '' });
      await load();
    } catch (err: any) { setError(err.message); }
  }

  function startEditContact(c: EmergencyContact) {
    setContactForm({ contactName: c.contactName, relationship: c.relationship, phone: c.phone, email: c.email || '', addressLine1: c.addressLine1 || '', addressLine2: c.addressLine2 || '', city: c.city || '', stateRegion: c.stateRegion || '', postalCode: c.postalCode || '', country: c.country || '' });
    setEditingContactId(c.emergencyContactId);
    setAddingContact(false);
  }

  async function saveEditContact() {
    if (!editingContactId) return;
    try {
      setError('');
      await api.put(`/api/employees/${employeeId}/emergency-contacts/${editingContactId}`, contactForm);
      setEditingContactId(null); setContactForm({ contactName: '', relationship: '', phone: '', email: '', addressLine1: '', addressLine2: '', city: '', stateRegion: '', postalCode: '', country: '' });
      await load();
    } catch (err: any) { setError(err.message); }
  }

  async function saveFamily() {
    try {
      setError('');
      await api.post(`/api/employees/${employeeId}/family`, {
        ...familyForm,
        dateOfBirth: familyForm.dateOfBirth || null,
        passportExpiry: familyForm.passportExpiry || null,
        passportNumber: familyForm.passportNumber || null,
        passportCountry: familyForm.passportCountry || null,
        nationalIdExpiry: familyForm.nationalIdExpiry || null,
        nationalIdNumber: familyForm.nationalIdNumber || null,
        nationalIdType: familyForm.nationalIdType || null,
      });
      setAddingFamily(false);
      setFamilyForm({ fullName: '', relationship: 'Spouse', dateOfBirth: '', gender: '', phone: '', passportNumber: '', passportExpiry: '', passportCountry: '', nationalIdNumber: '', nationalIdType: '', nationalIdExpiry: '' });
      await load();
    } catch (err: any) { setError(err.message); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      const ep = deleteTarget.type === 'contact'
        ? `/api/employees/${employeeId}/emergency-contacts/${deleteTarget.id}`
        : `/api/employees/${employeeId}/family/${deleteTarget.id}`;
      await api.delete(ep); setDeleteTarget(null); await load();
    } catch (err: any) { setError(err.message); setDeleteTarget(null); }
  }

  if (loading) return <LoadingSpinner message="Loading..." />;

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <Card>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Emergency Contacts</h3>
          {(isHR || isSelf) && <button className={styles.addSmallBtn} onClick={() => setAddingContact(true)}>+ Add</button>}
        </div>
        {addingContact && (
          <div className={styles.inlineForm}>
            <div className={styles.fieldGrid}>
              <FormInput label="Name *" value={contactForm.contactName} onChange={v => setContactForm({ ...contactForm, contactName: v })} />
              <FormInput label="Relationship *" value={contactForm.relationship} onChange={v => setContactForm({ ...contactForm, relationship: v })} placeholder="Spouse, Parent, Sibling..." />
              <FormInput label="Phone *" value={contactForm.phone} onChange={v => setContactForm({ ...contactForm, phone: v })} />
              <FormInput label="Email" value={contactForm.email} onChange={v => setContactForm({ ...contactForm, email: v })} />
            </div>
            <div style={{ margin: '8px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4A5568', cursor: 'pointer' }}>
                <input type="checkbox" onChange={e => {
                  if (e.target.checked && employeeAddress) {
                    setContactForm(prev => ({ ...prev, addressLine1: employeeAddress.addressLine1 || '', addressLine2: employeeAddress.addressLine2 || '', city: employeeAddress.city || '', stateRegion: employeeAddress.stateRegion || '', postalCode: employeeAddress.postalCode || '', country: employeeAddress.country || '' }));
                  }
                }} style={{ width: '14px', height: '14px' }} />
                Copy from employee's current address
              </label>
            </div>
            <div className={styles.fieldGrid}>
              <FormInput label="Address Line 1" value={contactForm.addressLine1} onChange={v => setContactForm({ ...contactForm, addressLine1: v })} />
              <FormInput label="Address Line 2" value={contactForm.addressLine2} onChange={v => setContactForm({ ...contactForm, addressLine2: v })} />
              <FormInput label="City" value={contactForm.city} onChange={v => setContactForm({ ...contactForm, city: v })} />
              <FormInput label="State / Region" value={contactForm.stateRegion} onChange={v => setContactForm({ ...contactForm, stateRegion: v })} />
              <FormInput label="Postal Code" value={contactForm.postalCode} onChange={v => setContactForm({ ...contactForm, postalCode: v })} />
              <FormInput label="Country" value={contactForm.country} onChange={v => setContactForm({ ...contactForm, country: v })} />
            </div>
            <div className={styles.inlineFormActions}>
              <button className={styles.cancelSmallBtn} onClick={() => setAddingContact(false)}>Cancel</button>
              <button className={styles.saveSmallBtn} onClick={saveContact}>Save</button>
            </div>
          </div>
        )}
        {contacts.length === 0 && !addingContact ? (
          <p className={styles.emptyText}>No emergency contacts recorded.</p>
        ) : (
          <div className={styles.subList}>
            {contacts.map(c => (
              <div key={c.emergencyContactId} className={styles.subListItem} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '6px' }}>
                {editingContactId === c.emergencyContactId ? (
                  <div className={styles.inlineForm}>
                    <div className={styles.fieldGrid}>
                      <FormInput label="Name *" value={contactForm.contactName} onChange={v => setContactForm({ ...contactForm, contactName: v })} />
                      <FormInput label="Relationship *" value={contactForm.relationship} onChange={v => setContactForm({ ...contactForm, relationship: v })} placeholder="Spouse, Parent, Sibling..." />
                      <FormInput label="Phone *" value={contactForm.phone} onChange={v => setContactForm({ ...contactForm, phone: v })} />
                      <FormInput label="Email" value={contactForm.email} onChange={v => setContactForm({ ...contactForm, email: v })} />
                    </div>
                    <div className={styles.fieldGrid}>
                      <FormInput label="Address Line 1" value={contactForm.addressLine1} onChange={v => setContactForm({ ...contactForm, addressLine1: v })} />
                      <FormInput label="Address Line 2" value={contactForm.addressLine2} onChange={v => setContactForm({ ...contactForm, addressLine2: v })} />
                      <FormInput label="City" value={contactForm.city} onChange={v => setContactForm({ ...contactForm, city: v })} />
                      <FormInput label="State / Region" value={contactForm.stateRegion} onChange={v => setContactForm({ ...contactForm, stateRegion: v })} />
                      <FormInput label="Postal Code" value={contactForm.postalCode} onChange={v => setContactForm({ ...contactForm, postalCode: v })} />
                      <FormInput label="Country" value={contactForm.country} onChange={v => setContactForm({ ...contactForm, country: v })} />
                    </div>
                    <div className={styles.inlineFormActions}>
                      <button className={styles.cancelSmallBtn} onClick={() => { setEditingContactId(null); setContactForm({ contactName: '', relationship: '', phone: '', email: '', addressLine1: '', addressLine2: '', city: '', stateRegion: '', postalCode: '', country: '' }); }}>Cancel</button>
                      <button className={styles.saveSmallBtn} onClick={saveEditContact}>Save</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div className={styles.subListName}>{c.contactName}</div>
                      <div className={styles.subListMeta}>{c.relationship} · {c.phone}{c.email ? ` · ${c.email}` : ''}</div>
                      {(c.addressLine1 || c.city || c.country) && (
                        <div className={styles.subListMeta}>{[c.addressLine1, c.addressLine2, c.city, c.stateRegion, c.postalCode, c.country].filter(Boolean).join(', ')}</div>
                      )}
                    </div>
                    {(isHR || isSelf) && (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button className={styles.addSmallBtn} onClick={() => startEditContact(c)} style={{ fontSize: '11px', padding: '3px 8px' }}>Edit</button>
                        <button className={styles.removeBtn} onClick={() => setDeleteTarget({ type: 'contact', id: c.emergencyContactId, name: c.contactName })}>Remove</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {!isManagerView && (
      <Card>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Family Members</h3>
          {isHR && <button className={styles.addSmallBtn} onClick={() => setAddingFamily(true)}>+ Add</button>}
        </div>
        {addingFamily && (
          <div className={styles.inlineForm}>
            <div className={styles.fieldGrid}>
              <FormInput label="Full Name *" value={familyForm.fullName} onChange={v => setFamilyForm({ ...familyForm, fullName: v })} />
              <div className={styles.formField}>
                <label className={styles.formLabel}>Relationship *</label>
                <select className={styles.formInput} value={familyForm.relationship} onChange={e => setFamilyForm({ ...familyForm, relationship: e.target.value })}>
                  <option value="Spouse">Spouse</option><option value="Partner">Partner</option><option value="Child">Child</option>
                </select>
              </div>
              <FormInput label="Date of Birth" type="date" value={familyForm.dateOfBirth} onChange={v => setFamilyForm({ ...familyForm, dateOfBirth: v })} />
              <FormInput label="Gender" value={familyForm.gender} onChange={v => setFamilyForm({ ...familyForm, gender: v })} placeholder="Male, Female" />
              <FormInput label="Phone" value={familyForm.phone} onChange={v => setFamilyForm({ ...familyForm, phone: v })} />
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#718096', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Passport</div>
              <div className={styles.fieldGrid}>
                <FormInput label="Passport Number" value={familyForm.passportNumber} onChange={v => setFamilyForm({ ...familyForm, passportNumber: v })} />
                <FormInput label="Expiry Date" type="date" value={familyForm.passportExpiry} onChange={v => setFamilyForm({ ...familyForm, passportExpiry: v })} />
                <FormInput label="Issuing Country" value={familyForm.passportCountry} onChange={v => setFamilyForm({ ...familyForm, passportCountry: v })} />
              </div>
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#718096', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>National ID</div>
              <div className={styles.fieldGrid}>
                <FormInput label="ID Type" value={familyForm.nationalIdType} onChange={v => setFamilyForm({ ...familyForm, nationalIdType: v })} placeholder="National ID, Residence Card..." />
                <FormInput label="ID Number" value={familyForm.nationalIdNumber} onChange={v => setFamilyForm({ ...familyForm, nationalIdNumber: v })} />
                <FormInput label="Expiry Date" type="date" value={familyForm.nationalIdExpiry} onChange={v => setFamilyForm({ ...familyForm, nationalIdExpiry: v })} />
              </div>
            </div>
            <div className={styles.inlineFormActions}>
              <button className={styles.cancelSmallBtn} onClick={() => setAddingFamily(false)}>Cancel</button>
              <button className={styles.saveSmallBtn} onClick={saveFamily}>Save</button>
            </div>
          </div>
        )}
        {family.length === 0 && !addingFamily ? (
          <p className={styles.emptyText}>No family members recorded.</p>
        ) : (
          <div className={styles.subList}>
            {family.map(fm => {
              const hasIds = fm.passportNumber || fm.nationalIdNumber;
              const expanded = expandedFamily === fm.familyMemberId;
              return (
                <div key={fm.familyMemberId} className={styles.subListItem} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div className={styles.subListName}>{fm.fullName}</div>
                      <div className={styles.subListMeta}>
                        {fm.relationship}{fm.dateOfBirth ? ` · Born ${formatDate(fm.dateOfBirth)}` : ''}{fm.gender ? ` · ${fm.gender}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {hasIds && (
                        <button className={styles.addSmallBtn} onClick={() => setExpandedFamily(expanded ? null : fm.familyMemberId)} style={{ fontSize: '11px', padding: '3px 8px' }}>
                          {expanded ? '▲ Hide IDs' : '▼ Show IDs'}
                        </button>
                      )}
                      {isHR && <button className={styles.removeBtn} onClick={() => setDeleteTarget({ type: 'family', id: fm.familyMemberId, name: fm.fullName })}>Remove</button>}
                    </div>
                  </div>
                  {expanded && (
                    <div style={{ background: '#F7FAFC', borderRadius: '6px', padding: '10px 12px', fontSize: '13px', color: '#4A5568', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                      {fm.passportNumber && (
                        <>
                          <div><span style={{ color: '#718096', fontSize: '11px' }}>Passport</span><br />{fm.passportNumber}{fm.passportCountry ? ` (${fm.passportCountry})` : ''}</div>
                          {fm.passportExpiry && <div><span style={{ color: '#718096', fontSize: '11px' }}>Passport Expiry</span><br />{formatDate(fm.passportExpiry)}</div>}
                        </>
                      )}
                      {fm.nationalIdNumber && (
                        <>
                          <div><span style={{ color: '#718096', fontSize: '11px' }}>{fm.nationalIdType || 'National ID'}</span><br />{fm.nationalIdNumber}</div>
                          {fm.nationalIdExpiry && <div><span style={{ color: '#718096', fontSize: '11px' }}>ID Expiry</span><br />{formatDate(fm.nationalIdExpiry)}</div>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      )}

      <ConfirmDialog open={deleteTarget !== null}
        title={`Remove ${deleteTarget?.type === 'contact' ? 'Emergency Contact' : 'Family Member'}`}
        message={`Remove ${deleteTarget?.name}? This cannot be undone.`}
        confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}

// =============================================================================
// Documents Tab — unchanged
// =============================================================================

const DOCUMENT_CATEGORIES = ['Contract', 'ID', 'Visa', 'Medical', 'BonusLetter', 'Certificate', 'Other'];

function DocumentsTab({ employeeId, isHR }: { employeeId: string; isHR: boolean }) {
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => { load(); }, [employeeId]);

  async function load() {
    try { setLoading(true); setDocs(await api.get<DocumentItem[]>(`/api/employees/${employeeId}/documents`)); }
    catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  async function handleUpload() {
    if (!selectedFile) { setError('Please select a file'); return; }
    if (selectedFile.size > 10 * 1024 * 1024) { setError('File size exceeds 10 MB limit'); return; }
    try {
      setUploading(true); setError('');
      const fd = new FormData();
      fd.append('file', selectedFile); fd.append('category', uploadCategory);
      if (uploadDescription) fd.append('description', uploadDescription);
      await api.upload(`/api/employees/${employeeId}/documents`, fd);
      setSelectedFile(null); setUploadCategory('Other'); setUploadDescription(''); setShowUpload(false);
      await load();
    } catch (err: any) { setError(err.message || 'Failed to upload'); }
    finally { setUploading(false); }
  }

  async function handleDownload(doc: DocumentItem) {
    try {
      const response = await fetch(`/api/employees/${employeeId}/documents/${doc.documentId}/download`, { headers: { 'Authorization': `Bearer ${await api.getToken()}` } });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = doc.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) { setError(err.message || 'Failed to download'); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/employees/${employeeId}/documents/${deleteTarget.id}`);
      setDeleteTarget(null); await load();
    } catch (err: any) { setError(err.message); setDeleteTarget(null); }
  }

  if (loading) return <LoadingSpinner message="Loading..." />;

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Documents</h3>
          {isHR && <button className={styles.addSmallBtn} onClick={() => setShowUpload(true)}>+ Upload Document</button>}
        </div>
        {showUpload && (
          <div className={styles.inlineForm}>
            <div className={styles.fieldGrid}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>File *</label>
                <input type="file" onChange={e => setSelectedFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv,.txt" style={{ fontSize: '13px' }} />
                <div style={{ fontSize: '11px', color: '#9BA5B0', marginTop: '4px' }}>PDF, DOC, DOCX, JPG, PNG, XLSX up to 10 MB</div>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Category *</label>
                <select className={styles.formInput} value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}>
                  {DOCUMENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <FormInput label="Description" value={uploadDescription} onChange={v => setUploadDescription(v)} placeholder="e.g. Passport copy, Employment contract..." />
            </div>
            <div className={styles.inlineFormActions}>
              <button className={styles.cancelSmallBtn} onClick={() => { setShowUpload(false); setSelectedFile(null); }}>Cancel</button>
              <button className={styles.saveSmallBtn} onClick={handleUpload} disabled={uploading || !selectedFile}>{uploading ? 'Uploading...' : 'Upload'}</button>
            </div>
          </div>
        )}
        {docs.length === 0 && !showUpload ? <p className={styles.emptyText}>No documents uploaded yet.</p> : (
          <div className={styles.subList}>
            {docs.map(d => (
              <div key={d.documentId} className={styles.subListItem}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => handleDownload(d)}>
                  <div className={styles.subListName} style={{ color: '#00B0B9', textDecoration: 'underline' }}>{d.fileName}</div>
                  <div className={styles.subListMeta}>{d.category}{d.description ? ` · ${d.description}` : ''} · {(d.fileSizeBytes / 1024).toFixed(0)} KB · Uploaded {formatDate(d.uploadedAt)} by {d.uploadedBy}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className={styles.addSmallBtn} onClick={() => handleDownload(d)} style={{ fontSize: '12px', padding: '4px 10px' }}>↓ Download</button>
                  {isHR && <button className={styles.removeBtn} onClick={() => setDeleteTarget({ id: d.documentId, name: d.fileName })}>Remove</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <ConfirmDialog open={deleteTarget !== null} title="Remove Document"
        message={`Remove ${deleteTarget?.name}? This will permanently delete the file.`}
        confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />

      <GeneratedLettersSection employeeId={employeeId} />
    </div>
  );
}

// ─── Generated Letters Section (inside Documents tab) ───

function GeneratedLettersSection({ employeeId }: { employeeId: string }) {
  const [letters, setLetters] = useState<GeneratedLetterItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLetters();
  }, [employeeId]);

  async function loadLetters() {
    try {
      setLoading(true);
      setLetters(await letterApi.getHistory(employeeId));
    } catch {
      // Silently fail — section is supplementary
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(letter: GeneratedLetterItem) {
    try {
      await letterApi.downloadGeneratedLetter(employeeId, letter.generatedLetterId, letter.fileName);
    } catch {
      alert('Download failed — the file may not be available in storage.');
    }
  }

  if (loading) return null;
  if (letters.length === 0) return null;

  return (
    <Card>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Generated Letters</h3>
      </div>
      <div className={styles.subList}>
        {letters.map(l => (
          <div key={l.generatedLetterId} className={styles.subListItem}>
            <div style={{ flex: 1 }}>
              <div className={styles.subListName}>{l.templateName}</div>
              <div className={styles.subListMeta}>
                {l.fileName} · Generated {new Date(l.generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} by {l.generatedByName} · Signed by {l.signatoryName}
              </div>
            </div>
            {l.hasFile && (
              <button className={styles.addSmallBtn} onClick={() => handleDownload(l)} style={{ fontSize: '12px', padding: '4px 10px' }}>↓ Download</button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

// =============================================================================
// IT Assets Tab — unchanged
// =============================================================================

interface AssetItem {
  assetId: string; employeeId: string; assetType: string; makeModel: string | null;
  serialNumber: string | null; assetTag: string | null; status: string; condition: string | null;
  assignedDate: string | null; returnedDate: string | null; purchaseDate: string | null;
  warrantyExpiryDate: string | null; notes: string | null; createdBy: string | null;
  createdAt: string; updatedBy: string | null; updatedAt: string | null;
}

const ASSET_TYPES = ['Laptop', 'Desktop', 'Monitor', 'Phone', 'Headset', 'Licence', 'AccessCard', 'Other'];
const ASSET_STATUSES = ['Assigned', 'Returned', 'Lost', 'Disposed'];
const ASSET_CONDITIONS = ['New', 'Good', 'Fair', 'Poor', 'Damaged'];
const ASSET_TYPE_ICONS: Record<string, string> = { Laptop: '💻', Desktop: '🖥️', Monitor: '🖥️', Phone: '📱', Headset: '🎧', Licence: '🔑', AccessCard: '🪪', Other: '📦' };

function AssetsTab({ employeeId, employee, isHR }: { employeeId: string; employee: EmployeeDetail; isHR: boolean }) {
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AssetItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { assetType: 'Laptop', makeModel: '', serialNumber: '', assetTag: '', status: 'Assigned', condition: 'New', assignedDate: new Date().toISOString().split('T')[0], returnedDate: '', purchaseDate: '', warrantyExpiryDate: '', notes: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, [employeeId]);
  async function load() {
    try { setLoading(true); setAssets(await api.get<AssetItem[]>(`/api/employees/${employeeId}/assets`)); }
    catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  function handleEdit(asset: AssetItem) {
    setEditingAsset(asset);
    setForm({ assetType: asset.assetType, makeModel: asset.makeModel || '', serialNumber: asset.serialNumber || '', assetTag: asset.assetTag || '', status: asset.status, condition: asset.condition || 'Good', assignedDate: asset.assignedDate ? asset.assignedDate.split('T')[0] : '', returnedDate: asset.returnedDate ? asset.returnedDate.split('T')[0] : '', purchaseDate: asset.purchaseDate ? asset.purchaseDate.split('T')[0] : '', warrantyExpiryDate: asset.warrantyExpiryDate ? asset.warrantyExpiryDate.split('T')[0] : '', notes: asset.notes || '' });
    setShowForm(true);
  }

  async function handleSave() {
    try {
      setSaving(true); setError('');
      const body = { ...form, assignedDate: form.assignedDate || null, returnedDate: form.returnedDate || null, purchaseDate: form.purchaseDate || null, warrantyExpiryDate: form.warrantyExpiryDate || null, makeModel: form.makeModel || null, serialNumber: form.serialNumber || null, assetTag: form.assetTag || null, notes: form.notes || null };
      if (editingAsset) { await api.put(`/api/employees/${employeeId}/assets/${editingAsset.assetId}`, body); }
      else { await api.post(`/api/employees/${employeeId}/assets`, body); }
      setShowForm(false); setEditingAsset(null); await load();
    } catch (err: any) { setError(err.message || 'Failed to save asset'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try { await api.delete(`/api/employees/${employeeId}/assets/${deleteTarget.id}`); setDeleteTarget(null); await load(); }
    catch (err: any) { setError(err.message); setDeleteTarget(null); }
  }

  const isExpired = (d: string) => new Date(d) < new Date();
  const isExpiring = (d: string) => { const dt = new Date(d); const n = new Date(); const n90 = new Date(); n90.setDate(n.getDate() + 90); return dt >= n && dt <= n90; };
  const statusClass = (s: string) => ({ Assigned: styles.assetStatusAssigned, PendingReturn: styles.assetStatusPending, Returned: styles.assetStatusReturned, Lost: styles.assetStatusLost, Disposed: styles.assetStatusDisposed }[s] || '');
  const condClass = (c: string) => ({ New: styles.condNew, Good: styles.condGood, Fair: styles.condFair, Poor: styles.condPoor, Damaged: styles.condDamaged }[c] || '');

  if (loading) return <LoadingSpinner message="Loading assets..." />;

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>IT Assets</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isHR && <button className={styles.addSmallBtn} onClick={() => { setEditingAsset(null); setForm(emptyForm); setShowForm(true); }}>+ Add Asset</button>}
            {assets.length > 0 && <button className={styles.addSmallBtn} style={{ background: '#0A2240', color: 'white', border: 'none' }} onClick={() => generateAssetPdf({ ...employee, fullName: employee.firstName + " " + employee.lastName }, assets)}>↓ Download PDF</button>}
          </div>
        </div>
        {showForm && (
          <div className={styles.inlineForm}>
            <div className={styles.fieldGrid}>
              <div className={styles.formField}><label className={styles.formLabel}>Asset Type *</label><select className={styles.formInput} value={form.assetType} onChange={e => setForm({ ...form, assetType: e.target.value })}>{ASSET_TYPES.map(t => <option key={t} value={t}>{ASSET_TYPE_ICONS[t]} {t}</option>)}</select></div>
              <FormInput label="Make / Model" value={form.makeModel} onChange={v => setForm({ ...form, makeModel: v })} placeholder="e.g. Dell XPS 15" />
              <FormInput label="Serial Number" value={form.serialNumber} onChange={v => setForm({ ...form, serialNumber: v })} />
              <FormInput label="Asset Tag" value={form.assetTag} onChange={v => setForm({ ...form, assetTag: v })} />
              <div className={styles.formField}><label className={styles.formLabel}>Status *</label><select className={styles.formInput} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>{ASSET_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div className={styles.formField}><label className={styles.formLabel}>Condition</label><select className={styles.formInput} value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })}>{ASSET_CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <FormInput label="Assigned Date" type="date" value={form.assignedDate} onChange={v => setForm({ ...form, assignedDate: v })} />
              <FormInput label="Purchase Date" type="date" value={form.purchaseDate} onChange={v => setForm({ ...form, purchaseDate: v })} />
              <FormInput label="Warranty Expiry" type="date" value={form.warrantyExpiryDate} onChange={v => setForm({ ...form, warrantyExpiryDate: v })} />
              <FormInput label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            </div>
            <div className={styles.inlineFormActions}>
              <button className={styles.cancelSmallBtn} onClick={() => { setShowForm(false); setEditingAsset(null); }}>Cancel</button>
              <button className={styles.saveSmallBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editingAsset ? 'Save Changes' : 'Add Asset'}</button>
            </div>
          </div>
        )}
        {assets.length === 0 && !showForm ? <p className={styles.emptyText}>No assets assigned to this employee.</p> : (
          <div className={styles.assetTableWrapper}>
            <table className={styles.assetTable}>
              <thead><tr><th>Type</th><th>Make / Model</th><th>Serial / Tag</th><th>Status</th><th>Condition</th><th>Assigned</th><th>Warranty</th>{isHR && <th>Actions</th>}</tr></thead>
              <tbody>
                {assets.map(a => (
                  <tr key={a.assetId}>
                    <td>{ASSET_TYPE_ICONS[a.assetType] || '📦'} {a.assetType}</td>
                    <td>{a.makeModel || '—'}</td>
                    <td className={styles.cellMono}>{a.serialNumber ? <div>{a.serialNumber}</div> : null}{a.assetTag ? <div style={{ fontSize: '11px', color: '#718096' }}>{a.assetTag}</div> : null}{!a.serialNumber && !a.assetTag ? '—' : null}</td>
                    <td><span className={`${styles.assetStatusBadge} ${statusClass(a.status)}`}>{a.status}</span></td>
                    <td>{a.condition ? <span className={`${styles.assetCondBadge} ${condClass(a.condition)}`}>{a.condition}</span> : '—'}</td>
                    <td className={styles.cellDate}>{formatDate(a.assignedDate)}</td>
                    <td>{a.warrantyExpiryDate ? <span className={isExpired(a.warrantyExpiryDate) ? styles.warrantyExpired : isExpiring(a.warrantyExpiryDate) ? styles.warrantyExpiring : styles.cellDate}>{formatDate(a.warrantyExpiryDate)}</span> : '—'}</td>
                    {isHR && <td><div style={{ display: 'flex', gap: '6px' }}><button className={styles.addSmallBtn} onClick={() => handleEdit(a)} style={{ fontSize: '11px', padding: '3px 8px' }}>Edit</button><button className={styles.removeBtn} onClick={() => setDeleteTarget({ id: a.assetId, name: `${a.assetType}${a.makeModel ? ` (${a.makeModel})` : ''}` })} style={{ fontSize: '11px', padding: '3px 8px' }}>Del</button></div></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <ConfirmDialog open={deleteTarget !== null} title="Remove Asset" message={`Remove ${deleteTarget?.name}? This cannot be undone.`} confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </div>
  );
}

// =============================================================================
// History Tab — unchanged
// =============================================================================

interface ChangeHistoryItem {
  changeHistoryId: string; fieldName: string; fieldLabel: string;
  oldValue: string | null; newValue: string | null; changedBy: string; changedByName: string; changedAt: string;
}

function HistoryTab({ employeeId }: { employeeId: string }) {
  const [changeHistory, setChangeHistory] = useState<ChangeHistoryItem[]>([]);
  const [salaryHistory, setSalaryHistory] = useState<SalaryHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, [employeeId]);

  async function load() {
    try {
      setLoading(true);
      const [changes, salary] = await Promise.all([
        api.get<ChangeHistoryItem[]>(`/api/employees/${employeeId}/change-history`),
        api.get<SalaryHistory[]>(`/api/employees/${employeeId}/salary-history`),
      ]);
      setChangeHistory(changes); setSalaryHistory(salary);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (loading) return <LoadingSpinner message="Loading..." />;

  const grouped: { date: string; changedByName: string; items: ChangeHistoryItem[] }[] = [];
  changeHistory.forEach(c => {
    const last = grouped.length > 0 ? grouped[grouped.length - 1] : null;
    if (last && Math.abs(new Date(last.date).getTime() - new Date(c.changedAt).getTime()) < 2000) { last.items.push(c); }
    else { grouped.push({ date: c.changedAt, changedByName: c.changedByName, items: [c] }); }
  });

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <h3 className={styles.sectionTitle}>Employee Change Log</h3>
        {changeHistory.length === 0 ? <p className={styles.emptyText}>No changes recorded yet.</p> : (
          <div className={styles.historyList}>
            {grouped.map((group, gi) => (
              <div key={gi} className={styles.historyItem}>
                <div className={styles.historyDot} style={{ backgroundColor: '#00B0B9' }} />
                <div className={styles.historyContent}>
                  <div className={styles.historyMeta} style={{ fontWeight: 600, marginBottom: '6px' }}>{formatDateTime(group.date)} · by {group.changedByName}</div>
                  {group.items.map(c => (
                    <div key={c.changeHistoryId} style={{ fontSize: '13px', color: '#4A5568', marginBottom: '3px' }}>
                      <strong>{c.fieldLabel}</strong>: {c.oldValue || '—'} → {c.newValue || '—'}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <h3 className={styles.sectionTitle}>Salary History</h3>
        {salaryHistory.length === 0 ? <p className={styles.emptyText}>No salary changes recorded yet.</p> : (
          <div className={styles.historyList}>
            {salaryHistory.map(h => (
              <div key={h.salaryHistoryId} className={styles.historyItem}>
                <div className={styles.historyDot} />
                <div className={styles.historyContent}>
                  <div className={styles.historyAmount}>{h.currency} {h.baseSalary.toLocaleString()}</div>
                  <div className={styles.historyMeta}>Effective {formatDate(h.effectiveDate)}{h.changeReason && ` · ${h.changeReason}`}</div>
                  <div className={styles.historyMeta}>Recorded {formatDateTime(h.changedAt)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// Compliance Tab (CM-10) — the person's own submission history (self only)
// =============================================================================

function ComplianceTab() {
  const [submissions, setSubmissions] = useState<ComplianceSubmissionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const data = await complianceApi.getMySubmissions();
      setSubmissions(data);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (loading) return <LoadingSpinner message="Loading..." />;

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <h3 className={styles.sectionTitle}>Compliance History</h3>
        {submissions.length === 0 ? (
          <p className={styles.emptyText}>No compliance submissions yet.</p>
        ) : (
          <div className={styles.historyList}>
            {submissions.map(sub => (
              <div key={sub.complianceSubmissionId} className={styles.historyItem}>
                <div className={styles.historyDot} style={{ backgroundColor: '#16A34A' }} />
                <div className={styles.historyContent}>
                  <div className={styles.historyMeta} style={{ fontWeight: 600, marginBottom: '4px' }}>
                    {sub.title}{' '}
                    <span style={{ fontWeight: 400, color: '#6B7885' }}>
                      &middot; {sub.submissionType} &middot; v{sub.versionNumber}
                    </span>
                  </div>
                  <div className={styles.historyMeta}>Submitted {formatDateTime(sub.submittedDate)}</div>
                  <button
                    onClick={() => setViewingId(sub.complianceSubmissionId)}
                    style={{ background: 'none', border: 'none', padding: 0, marginTop: '4px', fontSize: '13px', fontWeight: 600, color: '#0090A0', cursor: 'pointer' }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {viewingId && (
        <SubmissionViewer submissionId={viewingId} onClose={() => setViewingId(null)} />
      )}
    </div>
  );
}

// =============================================================================
// Shared Components
// =============================================================================

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fieldItem}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{value || '—'}</div>
    </div>
  );
}

function FormInput({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}</label>
      <input className={styles.formInput} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function EditInput({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}</label>
      <input className={styles.formInput} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function EditSelect({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div className={styles.formField}>
      <label className={styles.formLabel}>{label}</label>
      <select className={styles.formInput} value={value} onChange={e => onChange(e.target.value)}>{children}</select>
    </div>
  );
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
